/*
 * GoldATM — Interactive Stack Builder (Option B)
 * Turns the server-rendered single-note pool into a filterable grid + live stack
 * summary, then adds every chosen note to cart with a shared `selling_plan` id
 * (one cadence per subscription). Loop manages the resulting contracts; this file
 * never talks to Loop — it only builds a standard /cart/add.js payload.
 *
 * Progressive enhancement: if this script never runs, the section's static
 * shoppable grid stays visible and nothing is hidden.
 */
(function () {
  'use strict';

  // Denomination display + sort order (Goldback denominations, not dollars).
  var DENOM = {
    quarter: { label: '¼', order: 0 },
    half: { label: '½', order: 1 },
    '1': { label: '1', order: 2 },
    '2': { label: '2', order: 3 },
    '5': { label: '5', order: 4 },
    '10': { label: '10', order: 5 },
    '25': { label: '25', order: 6 },
    '50': { label: '50', order: 7 },
    '100': { label: '100', order: 8 }
  };

  var STATE = {
    arizona: 'Arizona',
    florida: 'Florida',
    idaho: 'Idaho',
    nevada: 'Nevada',
    'new-hampshire': 'New Hampshire',
    oklahoma: 'Oklahoma',
    'south-dakota': 'South Dakota',
    utah: 'Utah',
    wyoming: 'Wyoming'
  };

  var CADENCES = [
    { key: 'monthly', label: 'Monthly', word: 'month' },
    { key: 'every2', label: 'Every 2 months', word: '2 months' },
    { key: 'every3', label: 'Every 3 months', word: '3 months' }
  ];

  // Preset definitions. Filters pre-select chips; qty seeds the stepper for every
  // matching in-stock note. Starting points only — the customer edits freely.
  var PRESETS = {
    all_1s: { denom: ['1'], qtyEach: 3 },
    all_5s: { denom: ['5'], qtyEach: 1 },
    all_2s: { denom: ['2'], qtyEach: 2 },
    state_collector: { state: ['idaho'], qtyEach: 1 }, // one of each denom in one state
    idaho_quarter: { state: ['idaho'], denom: ['quarter', 'half', '1', '2'], qtyEach: 1 },
    mixed_stack: { pickDenoms: ['1', '5', '10', '25', '50', '100'], qtyEach: 1 } // one note per denom
  };

  // ---- Money formatting (classic Shopify money_format handler) ----
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '${{amount}}';

    function defaultTo(num, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(num) || num == null) return 0;
      num = (num / 100.0).toFixed(precision);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    switch ((format.match(placeholder) || [])[1]) {
      case 'amount':
        value = defaultTo(cents, 2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = defaultTo(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = defaultTo(cents, 0, ' ');
        break;
      default:
        value = defaultTo(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function denomLabel(slug) {
    return DENOM[slug] ? DENOM[slug].label : slug;
  }
  function denomOrder(slug) {
    return DENOM[slug] ? DENOM[slug].order : 99;
  }
  function stateLabel(slug) {
    return STATE[slug] || slug;
  }

  function init(root) {
    if (root.dataset.sbInit) return;
    root.dataset.sbInit = '1';

    var notesEl = root.querySelector('[data-sb-notes]');
    var configEl = root.querySelector('[data-sb-config]');
    if (!notesEl || !configEl) return;

    var notes, config;
    try {
      notes = JSON.parse(notesEl.textContent);
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return; // leave static fallback in place
    }
    if (!Array.isArray(notes) || !notes.length) return;

    // Normalise plan ids to numbers.
    var plans = {
      monthly: parseInt(config.plans.monthly, 10),
      every2: parseInt(config.plans.every2, 10),
      every3: parseInt(config.plans.every3, 10)
    };
    var money = function (c) { return formatMoney(c, config.moneyFormat); };
    var minCents = parseInt(config.minCents, 10) || 2500;
    var giftCents = parseInt(config.giftCents, 10) || 15000;
    var discountCents = parseInt(config.discountCents, 10) || 35000;
    var freeShipCents = parseInt(config.freeShipCents, 10) || 19900;

    // ---- State ----
    var qty = {};                 // variantId -> quantity
    var activeDenoms = {};        // slug -> true
    var activeStates = {};        // slug -> true
    var cadence = 'monthly';

    // ---- Elements ----
    var cadenceWrap = root.querySelector('[data-sb-cadence]');
    var denomWrap = root.querySelector('[data-sb-denom-filters]');
    var stateWrap = root.querySelector('[data-sb-state-filters]');
    var grid = root.querySelector('[data-sb-grid]');
    var summary = root.querySelector('[data-sb-summary]');
    var linesEl = root.querySelector('[data-sb-lines]');
    var linesWrap = root.querySelector('[data-sb-lines-wrap]');
    var linesEmpty = root.querySelector('[data-sb-lines-empty]');
    var countEl = root.querySelector('[data-sb-count]');
    var subtotalEl = root.querySelector('[data-sb-subtotal]');
    var cadenceSummary = root.querySelector('[data-sb-cadence-summary]');
    var minWrap = root.querySelector('[data-sb-min]');
    var minFill = root.querySelector('[data-sb-min-fill]');
    var minText = root.querySelector('[data-sb-min-text]');
    var perksEl = root.querySelector('[data-sb-perks]');
    var submitBtn = root.querySelector('[data-sb-submit]');
    var errorEl = root.querySelector('[data-sb-error]');
    // Mobile sticky bar + slide-up sheet
    var mobileBar = root.querySelector('[data-sb-mobile-bar]');
    var barCount = root.querySelector('[data-sb-bar-count]');
    var barTotal = root.querySelector('[data-sb-bar-total]');
    var barToggle = root.querySelector('[data-sb-bar-toggle]');
    var sheetClose = root.querySelector('[data-sb-sheet-close]');
    var sheetBackdrop = root.querySelector('[data-sb-sheet-backdrop]');
    var MOBILE_MQ = window.matchMedia('(max-width: 989px)');

    // Distinct denom/state slugs present in the pool, in canonical order.
    var denomSlugs = [];
    var stateSlugs = [];
    notes.forEach(function (n) {
      if (n.denom && denomSlugs.indexOf(n.denom) === -1) denomSlugs.push(n.denom);
      if (n.state && stateSlugs.indexOf(n.state) === -1) stateSlugs.push(n.state);
    });
    denomSlugs.sort(function (a, b) { return denomOrder(a) - denomOrder(b); });
    stateSlugs.sort(function (a, b) { return stateLabel(a).localeCompare(stateLabel(b)); });

    // ---------- Builders ----------
    function buildCadence() {
      CADENCES.forEach(function (c) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sb__cadence-opt';
        btn.textContent = c.label;
        btn.setAttribute('data-cadence', c.key);
        btn.setAttribute('aria-pressed', c.key === cadence ? 'true' : 'false');
        btn.addEventListener('click', function () {
          cadence = c.key;
          Array.prototype.forEach.call(cadenceWrap.children, function (el) {
            el.setAttribute('aria-pressed', el.getAttribute('data-cadence') === cadence ? 'true' : 'false');
          });
          renderSummary();
        });
        cadenceWrap.appendChild(btn);
      });
    }

    function makeChip(label, isAll, pressed, onClick) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sb__chip';
      btn.textContent = label;
      btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      if (isAll) btn.setAttribute('data-all', 'true');
      btn.addEventListener('click', onClick);
      return btn;
    }

    function buildFilters() {
      // Denomination
      denomWrap.appendChild(makeChip('All', true, true, function () { clearGroup(activeDenoms, denomWrap); }));
      denomSlugs.forEach(function (slug) {
        denomWrap.appendChild(makeChip(denomLabel(slug), false, false, function () {
          toggleFilter(activeDenoms, slug, denomWrap);
        }));
        denomWrap.lastChild.setAttribute('data-value', slug);
      });
      // State
      stateWrap.appendChild(makeChip('All', true, true, function () { clearGroup(activeStates, stateWrap); }));
      stateSlugs.forEach(function (slug) {
        stateWrap.appendChild(makeChip(stateLabel(slug), false, false, function () {
          toggleFilter(activeStates, slug, stateWrap);
        }));
        stateWrap.lastChild.setAttribute('data-value', slug);
      });
    }

    function syncChips(activeMap, wrap) {
      var anyActive = Object.keys(activeMap).length > 0;
      Array.prototype.forEach.call(wrap.children, function (el) {
        if (el.getAttribute('data-all')) {
          el.setAttribute('aria-pressed', anyActive ? 'false' : 'true');
        } else {
          var val = el.getAttribute('data-value');
          el.setAttribute('aria-pressed', activeMap[val] ? 'true' : 'false');
        }
      });
    }

    function toggleFilter(activeMap, slug, wrap) {
      if (activeMap[slug]) delete activeMap[slug];
      else activeMap[slug] = true;
      syncChips(activeMap, wrap);
      renderGrid();
    }

    function clearGroup(activeMap, wrap) {
      Object.keys(activeMap).forEach(function (k) { delete activeMap[k]; });
      syncChips(activeMap, wrap);
      renderGrid();
    }

    function noteVisible(n) {
      var dOk = !Object.keys(activeDenoms).length || activeDenoms[n.denom];
      var sOk = !Object.keys(activeStates).length || activeStates[n.state];
      return dOk && sOk;
    }

    // ---------- Grid ----------
    function noteCard(n) {
      var card = document.createElement('div');
      card.className = 'sb-card';
      card.setAttribute('data-id', n.id);
      if (qty[n.id] > 0) card.classList.add('sb-card--active');

      var media = document.createElement('div');
      media.className = 'sb-card__media';
      if (n.denom) {
        var badge = document.createElement('span');
        badge.className = 'sb-card__denom';
        badge.textContent = denomLabel(n.denom);
        media.appendChild(badge);
      }
      if (n.image) {
        var img = document.createElement('img');
        img.src = n.image;
        img.alt = n.title;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      if (!n.available) {
        var oos = document.createElement('span');
        oos.className = 'sb-card__oos';
        oos.textContent = 'Sold out';
        media.appendChild(oos);
      }
      card.appendChild(media);

      var body = document.createElement('div');
      body.className = 'sb-card__body';
      var name = document.createElement('span');
      name.className = 'sb-card__state';
      name.textContent = n.state ? stateLabel(n.state) : n.title;
      var price = document.createElement('span');
      price.className = 'sb-card__price';
      price.textContent = n.priceLabel || money(n.price);
      body.appendChild(name);
      body.appendChild(price);

      if (n.available) {
        body.appendChild(buildStepper(n));
      } else {
        var link = document.createElement('a');
        link.href = n.url;
        link.className = 'sb-card__price';
        link.textContent = 'View note';
        body.appendChild(link);
      }
      card.appendChild(body);
      return card;
    }

    function buildStepper(n) {
      var wrap = document.createElement('div');
      wrap.className = 'sb-stepper';

      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'sb-stepper__btn';
      minus.textContent = '−';
      minus.setAttribute('aria-label', 'Remove one ' + (n.state ? stateLabel(n.state) + ' ' : '') + denomLabel(n.denom) + ' Goldback');
      minus.disabled = !(qty[n.id] > 0);

      var input = document.createElement('input');
      input.type = 'number';
      input.className = 'sb-stepper__input';
      input.min = '0';
      input.step = '1';
      input.inputMode = 'numeric';
      input.value = qty[n.id] || 0;
      input.setAttribute('aria-label', 'Quantity of ' + (n.state ? stateLabel(n.state) + ' ' : '') + denomLabel(n.denom) + ' Goldback');

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'sb-stepper__btn';
      plus.textContent = '+';
      plus.setAttribute('aria-label', 'Add one ' + (n.state ? stateLabel(n.state) + ' ' : '') + denomLabel(n.denom) + ' Goldback');

      function setQty(val) {
        val = Math.max(0, parseInt(val, 10) || 0);
        if (val > 0) qty[n.id] = val; else delete qty[n.id];
        input.value = val;
        minus.disabled = val <= 0;
        var card = wrap.closest('.sb-card');
        if (card) card.classList.toggle('sb-card--active', val > 0);
        renderSummary();
      }

      minus.addEventListener('click', function () { setQty((qty[n.id] || 0) - 1); });
      plus.addEventListener('click', function () { setQty((qty[n.id] || 0) + 1); });
      input.addEventListener('change', function () { setQty(input.value); });

      wrap.appendChild(minus);
      wrap.appendChild(input);
      wrap.appendChild(plus);
      return wrap;
    }

    function renderGrid() {
      grid.innerHTML = '';
      var visible = notes.filter(noteVisible);
      // in-stock first, then canonical denom order, then state
      visible.sort(function (a, b) {
        if (a.available !== b.available) return a.available ? -1 : 1;
        if (denomOrder(a.denom) !== denomOrder(b.denom)) return denomOrder(a.denom) - denomOrder(b.denom);
        return stateLabel(a.state).localeCompare(stateLabel(b.state));
      });
      if (!visible.length) {
        var empty = document.createElement('p');
        empty.className = 'sb__empty';
        empty.textContent = 'No notes match those filters. Clear a filter to see more.';
        grid.appendChild(empty);
        return;
      }
      visible.forEach(function (n) { grid.appendChild(noteCard(n)); });
    }

    // ---------- Summary ----------
    function currentCadence() {
      for (var i = 0; i < CADENCES.length; i++) if (CADENCES[i].key === cadence) return CADENCES[i];
      return CADENCES[0];
    }

    function updateScrollCue() {
      if (!linesWrap || !linesEl) return;
      var scrollable = linesEl.scrollHeight - linesEl.clientHeight > 2;
      var atTop = linesEl.scrollTop <= 1;
      var atBottom = linesEl.scrollTop + linesEl.clientHeight >= linesEl.scrollHeight - 2;
      linesWrap.classList.toggle('can-scroll-up', scrollable && !atTop);
      linesWrap.classList.toggle('can-scroll-down', scrollable && !atBottom);
    }

    if (linesEl) {
      linesEl.addEventListener('scroll', updateScrollCue, { passive: true });
      window.addEventListener('resize', updateScrollCue, { passive: true });
    }

    function renderSummary() {
      var byId = {};
      notes.forEach(function (n) { byId[n.id] = n; });

      linesEl.innerHTML = '';
      var totalCount = 0;
      var subtotal = 0;
      var ids = Object.keys(qty);

      ids.forEach(function (id) {
        var n = byId[id];
        if (!n) return;
        var q = qty[id];
        totalCount += q;
        subtotal += q * n.price;

        var li = document.createElement('li');
        li.className = 'sb__line';
        var name = document.createElement('span');
        name.className = 'sb__line-name';
        name.innerHTML = '';
        var main = document.createTextNode(q + ' × ' + denomLabel(n.denom) + ' Goldback');
        name.appendChild(main);
        var sub = document.createElement('span');
        sub.className = 'sb__line-sub';
        sub.textContent = n.state ? stateLabel(n.state) : n.title;
        name.appendChild(sub);
        var amt = document.createElement('span');
        amt.className = 'sb__line-amt';
        amt.textContent = money(q * n.price);
        li.appendChild(name);
        li.appendChild(amt);
        linesEl.appendChild(li);
      });

      var hasLines = totalCount > 0;
      linesEmpty.hidden = hasLines;
      if (linesWrap) linesWrap.style.display = hasLines ? '' : 'none';
      updateScrollCue();
      countEl.textContent = totalCount;
      subtotalEl.textContent = money(subtotal);

      // Mirror into the mobile sticky bar
      if (barCount) barCount.textContent = totalCount + (totalCount === 1 ? ' note' : ' notes');
      if (barTotal) barTotal.textContent = money(subtotal);
      if (barToggle) barToggle.textContent = (totalCount > 0 && subtotal > minCents) ? 'Review & subscribe' : 'View stack';

      var cad = currentCadence();
      cadenceSummary.textContent = hasLines ? 'Ships every ' + cad.word : '';

      // Minimum progress
      var meetsMin = subtotal > minCents;
      if (hasLines && !meetsMin) {
        minWrap.hidden = false;
        var pct = Math.min(100, Math.round((subtotal / minCents) * 100));
        minFill.style.width = pct + '%';
        minText.textContent = 'Add ' + money(minCents - subtotal + 1) + ' more to start your subscription';
      } else {
        minWrap.hidden = true;
      }

      renderPerks(subtotal, hasLines);

      submitBtn.disabled = !(hasLines && meetsMin);
      hideError();
    }

    // Line-art icons matching the homepage trust-strip (24x24, currentColor,
    // stroke 1.7, round caps). Keyed so we never render raw emoji.
    var PERK_ICONS = {
      ship: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M1 7h12v9H1z"/><path d="M13 10h4l3 3v3h-7z"/><circle cx="5.5" cy="18.5" r="1.7"/><circle cx="17.5" cy="18.5" r="1.7"/></svg>',
      cycle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-7.5-4M3 12a9 9 0 0 1 9-9 9 9 0 0 1 7.5 4"/><path d="M20 3v4h-4M4 21v-4h4"/></svg>',
      purity: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.4 5 5.6.6-4.2 3.8 1.2 5.6L12 19l-5 3 1.2-5.6L4 12.6 9.6 12z"/></svg>'
    };
    function perkItem(iconKey, text, state) {
      var li = document.createElement('li');
      li.className = 'sb__perk' + (state ? ' sb__perk--' + state : '');
      var m = document.createElement('span');
      m.className = 'sb__perk-mark';
      m.setAttribute('aria-hidden', 'true');
      m.innerHTML = PERK_ICONS[iconKey] || '';
      var t = document.createElement('span');
      t.textContent = text;
      li.appendChild(m);
      li.appendChild(t);
      return li;
    }

    function renderPerks(subtotal, hasLines) {
      perksEl.innerHTML = '';
      // Free insured shipping over the threshold; flat Economy rate under it.
      if (subtotal >= freeShipCents) {
        perksEl.appendChild(perkItem('ship', 'Free insured shipping unlocked on this stack', 'unlocked'));
      } else if (hasLines) {
        var remain = freeShipCents - subtotal;
        perksEl.appendChild(perkItem('ship', 'Add ' + money(remain) + ' more for free insured shipping (' + money(freeShipCents) + '+)', 'locked'));
      } else {
        perksEl.appendChild(perkItem('ship', 'Free insured shipping on stacks ' + money(freeShipCents) + ' and up', 'locked'));
      }
      // Always-on perks
      perksEl.appendChild(perkItem('cycle', 'Change, skip, or cancel any time from your account', 'unlocked'));
      perksEl.appendChild(perkItem('purity', 'Every note is real 24K gold, priced to the live market', 'unlocked'));
    }

    // ---------- Presets ----------
    function applyPreset(name) {
      var p = PRESETS[name];
      if (!p) return;

      // reset
      Object.keys(qty).forEach(function (k) { delete qty[k]; });
      Object.keys(activeDenoms).forEach(function (k) { delete activeDenoms[k]; });
      Object.keys(activeStates).forEach(function (k) { delete activeStates[k]; });

      if (p.denom) p.denom.forEach(function (d) { if (denomSlugs.indexOf(d) !== -1) activeDenoms[d] = true; });
      if (p.state) p.state.forEach(function (s) { if (stateSlugs.indexOf(s) !== -1) activeStates[s] = true; });

      var qtyEach = p.qtyEach || 1;

      if (p.pickDenoms) {
        // one available note per listed denom
        p.pickDenoms.forEach(function (d) {
          var match = notes.filter(function (n) { return n.denom === d && n.available; });
          if (match.length) qty[match[0].id] = qtyEach;
        });
      } else {
        // seed every in-stock note that survives the preset's filters
        notes.forEach(function (n) {
          if (!n.available) return;
          var dOk = !p.denom || p.denom.indexOf(n.denom) !== -1;
          var sOk = !p.state || p.state.indexOf(n.state) !== -1;
          if (dOk && sOk) qty[n.id] = qtyEach;
        });
      }

      syncChips(activeDenoms, denomWrap);
      syncChips(activeStates, stateWrap);
      renderGrid();
      renderSummary();
    }

    // ---------- Mobile sheet ----------
    // NOTE: we deliberately do NOT lock background scroll with overflow:hidden.
    // On iOS Safari, toggling overflow on <html>/<body> reliably wedges the
    // momentum scroller so the page can't scroll after the sheet closes. The
    // dim backdrop already blocks background interaction, so a scroll lock is
    // unnecessary. Keeping the page scrollable is the safe, bug-free choice.
    function openSheet() {
      if (!summary) return;
      summary.classList.add('sb-sheet-open');
      if (sheetBackdrop) { sheetBackdrop.hidden = false; sheetBackdrop.classList.add('is-visible'); }
      updateScrollCue();
    }
    function closeSheet() {
      if (!summary) return;
      summary.classList.remove('sb-sheet-open');
      if (sheetBackdrop) { sheetBackdrop.classList.remove('is-visible'); sheetBackdrop.hidden = true; }
      // Defensive: clear any stale overflow lock left by earlier builds.
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }
    // Hide overlapping page chrome (chat bubble, email popups) on mobile — these
    // sit at max z-index and can't be reliably beaten from a stylesheet, so we
    // force it inline. Restore on desktop where the bar isn't used.
    // NOTE: Shopify ships its chat widget as a bare <shopify-chat> CUSTOM ELEMENT
    // (no id, no class) at z-index 2147483000. The `shopify-chat` TAG selector is
    // required — the old `.shopify-chat` CLASS selector no longer matches and the
    // bubble was colliding with the sticky stack bar + signup close X on mobile.
    var CHROME_SEL = 'shopify-chat, #ShopifyChat, inbox-online-store-chat, .shopify-chat, [id*="klaviyo-form"], .klaviyo-form, .needsclick[class*="kl-"], .newsletter-popup, .popup-modal, [data-shopify-forms-modal]';
    function suppressChrome(hide) {
      var els = document.querySelectorAll(CHROME_SEL);
      Array.prototype.forEach.call(els, function (el) {
        if (hide) {
          if (el.dataset.sbHidden) return;
          el.dataset.sbHidden = '1';
          // Some widgets (Shopify chat) pin `display:block !important` via a
          // shadow-DOM :host rule that beats any outer/inline display override.
          // So hide it without touching `display`: kill visibility, interaction,
          // and shove it off-screen.
          el.style.setProperty('visibility', 'hidden', 'important');
          el.style.setProperty('opacity', '0', 'important');
          el.style.setProperty('pointer-events', 'none', 'important');
          el.style.setProperty('transform', 'translateY(200%)', 'important');
        } else if (el.dataset.sbHidden) {
          delete el.dataset.sbHidden;
          el.style.removeProperty('visibility');
          el.style.removeProperty('opacity');
          el.style.removeProperty('pointer-events');
          el.style.removeProperty('transform');
        }
      });
    }
    function syncMobileBar() {
      if (!mobileBar) return;
      var onMobile = MOBILE_MQ.matches;
      mobileBar.classList.toggle('is-visible', onMobile);
      mobileBar.setAttribute('aria-hidden', onMobile ? 'false' : 'true');
      suppressChrome(onMobile);
      if (!onMobile) closeSheet(); // never leave the sheet stuck open when resizing to desktop
    }
    // Popups/chat can inject AFTER load; re-apply suppression (debounced) on mobile.
    var _chromeTimer = null;
    var _chromeObserver = new MutationObserver(function () {
      if (!MOBILE_MQ.matches) return;
      if (_chromeTimer) return;
      _chromeTimer = setTimeout(function () { _chromeTimer = null; suppressChrome(true); }, 300);
    });
    try { _chromeObserver.observe(document.body, { childList: true }); } catch (e) {}
    if (barToggle) barToggle.addEventListener('click', openSheet);
    if (sheetClose) sheetClose.addEventListener('click', closeSheet);
    if (sheetBackdrop) sheetBackdrop.addEventListener('click', closeSheet);
    if (MOBILE_MQ.addEventListener) MOBILE_MQ.addEventListener('change', syncMobileBar);
    else if (MOBILE_MQ.addListener) MOBILE_MQ.addListener(syncMobileBar);

    // ---------- Cart ----------
    function showError(msg) { errorEl.textContent = msg; errorEl.hidden = false; }
    function hideError() { errorEl.hidden = true; errorEl.textContent = ''; }

    function addToSubscription() {
      var plan = plans[cadence];
      var items = Object.keys(qty)
        .filter(function (id) { return qty[id] > 0; })
        .map(function (id) {
          return { id: parseInt(id, 10), quantity: qty[id], selling_plan: plan };
        });

      if (!items.length) { showError('Add at least one note first.'); return; }

      submitBtn.disabled = true;
      var original = submitBtn.textContent;
      submitBtn.textContent = 'Adding…';
      hideError();

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || 'Could not add your stack.');
            return data;
          });
        })
        .then(function () {
          window.location.href = config.cartRedirect || '/cart';
        })
        .catch(function (err) {
          submitBtn.disabled = false;
          submitBtn.textContent = original;
          showError(err.message || 'Something went wrong. Please try again.');
        });
    }

    // ---------- Wire up ----------
    buildCadence();
    buildFilters();
    renderGrid();
    renderSummary();
    submitBtn.addEventListener('click', addToSubscription);

    // reveal enhanced UI
    grid.hidden = false;
    summary.hidden = false;
    root.classList.add('sb--ready');
    // Mark the document so page-level CSS can suppress overlapping chrome
    // (chat bubble, email-signup popups) that would cover the sticky stack bar.
    document.documentElement.classList.add('sb-active');
    document.body.classList.add('sb-active');
    syncMobileBar();

    // Preset deep-link (?preset=all_1s)
    var preset = new URLSearchParams(window.location.search).get('preset');
    if (preset && PRESETS[preset]) {
      applyPreset(preset);
      // scroll builder into view after layout settles
      window.requestAnimationFrame(function () {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-stack-builder]').forEach(function (root) { init(root); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
